<template>
  <modal v-on:cancel="closeModal">
    <template v-slot:title>
      <p class="modal-card-title">Update Contact Attribute</p>
    </template>

    <template v-slot:body>
      <fieldset>
        <div class="field">
          <label class="label">ID</label>
          <ReadonlyField :readonlyValue="attribute.id" />
        </div>
        <div class="field">
          <label class="label" for="name">Name</label>
          <div class="control">
            <input
              ref="nameInput"
              type="text"
              class="input"
              id="name"
              autocomplete="off"
              v-model="request.name"
              @input="$v.request.name.$touch()"
            />
            <p class="help is-danger" v-if="isFieldNotPopulated($v.request, 'name')">Name is required</p>
            <p class="help is-danger" v-else-if="isFieldInvalid($v.request, 'name')">
              Name may only contain letters, numbers, spaces, underscores or hyphens, and must be no longer than 50
              characters
            </p>
          </div>
        </div>
        <div class="field">
          <label class="label" for="description">Description</label>
          <div class="control">
            <textarea
              ref="descriptionInput"
              class="textarea"
              id="description"
              v-model="request.description"
              @input="$v.request.description.$touch()"
            />
            <p class="help is-danger" v-if="isFieldNotPopulated($v.request, 'description')">Description is required</p>
            <p class="help is-danger" v-else-if="isFieldInvalid($v.request, 'description')">
              Description must be no longer than 255 characters
            </p>
          </div>
        </div>
        <div class="field">
          <label class="label">Data type</label>
          <ReadonlyField :readonlyValue="attributeType" />
        </div>
      </fieldset>
    </template>

    <template v-slot:buttons>
      <n-button-group>
        <n-button
          id="create-attribute-submit-btn"
          type="button"
          variant="primary"
          :disabled="submitDisabled"
          :loading="updateInProgress"
          @click="update"
          >Update Attribute</n-button
        >
        <n-button @click="closeModal" variant="primary--outline">Cancel</n-button>
      </n-button-group>
    </template>
  </modal>
</template>

<script lang="ts" setup>
import Toast from '@/shared/services/toast';
import ReadonlyField from '@/shared/components/ReadonlyField.vue';
import Modal from '@/shared/components/Modal.vue';
import { UpdateContactAttributeViewModel } from '@/messaging/models/updateContactAttributeViewModel';
import { ContactAttributeViewModel } from '@/messaging/models/contactAttributeViewModel';
import { ContactAttributeType } from '@/messaging/models/contactAttributeType';
import { UPDATE_CONTACT_ATTRIBUTE } from '@/store/attributes/actionTypes';
import { computed, onMounted, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';
import useVuelidate from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';

const props = defineProps({
  attribute: {
    type: Object as PropType<ContactAttributeViewModel>,
    required: true,
  },
});

const store = useStore();
const updateAttribute = (request: {
  attributeId: string;
  request: UpdateContactAttributeViewModel;
}): Promise<ContactAttributeViewModel> => {
  return store.dispatch(UPDATE_CONTACT_ATTRIBUTE, request);
};

const request = ref<UpdateContactAttributeViewModel>({ description: '', name: '' });
const updateInProgress = ref(false);
const nameInput = ref<HTMLInputElement | null>(null);

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'update', contact: ContactAttributeViewModel): void;
}>();

const submitDisabled = computed(() => {
  return (
    $v.value.$invalid ||
    (props.attribute.description === request.value.description && props.attribute.name === request.value.name)
  );
});

const attributeType = computed(() => {
  return Object.keys(ContactAttributeType).filter((x) => (ContactAttributeType as any)[x] === props.attribute.type)[0];
});

const validName = (value: string) => value.search(/[^a-zA-Z0-9 _\-]/) === -1;
const validations = {
  request: {
    description: {
      required,
      maxLength: maxLength(255),
    },
    name: {
      required,
      validName,
      maxLength: maxLength(50),
    },
  },
};

const $v = useVuelidate(validations, { request });

watch(
  () => props.attribute,
  (to) => {
    request.value.description = to.description;
    request.value.name = to.name;
  },
  { immediate: true },
);

onMounted(() => {
  if (nameInput.value) {
    nameInput.value.focus();
  }
});

function closeModal() {
  emit('cancel');
}

function isFieldNotPopulated(vuelidateObject: any, fieldName: string): boolean {
  const field = vuelidateObject[fieldName];
  return !field || (field.$invalid && !field.required && field.$dirty);
}

function isFieldInvalid(vuelidateObject: any, fieldName: string): boolean {
  const field = vuelidateObject[fieldName];
  return !field || (field.$invalid && field.$dirty);
}

async function update() {
  if (updateInProgress.value) {
    return;
  }

  updateInProgress.value = true;
  try {
    const updatedAttribute = await updateAttribute({ attributeId: props.attribute.id, request: request.value });
    Toast.showSuccess('Attribute successfully updated');
    emit('update', updatedAttribute);
  } catch (err) {
    Toast.showError(err.message);
  } finally {
    updateInProgress.value = false;
  }
}
</script>
